// 11 services — same sector as Cito (technology & internet services).
// Copy is freshly written for BTCRuka — engineering-led, terminal-flavoured.

const services = [
  {
    slug: "crm",
    code: "0x01",
    title: "CRM Solutions",
    blurb: "Customer-relationship platforms tuned to how your commercial teams actually sell and renew.",
    intro: "A CRM is the operating system of a commercial organisation. We deploy, customise and integrate it so that lead handoffs, pipeline forecasts and post-sale retention all sit on the same source of truth.",
    body: [
      "Most CRM programmes fail in adoption, not in technology. Reps refuse to log activity into a system that has been configured against the way they actually work; managers stop trusting forecasts that drift from observed reality; and the platform becomes another data island that no one wants to clean up.",
      "BTCRuka treats CRM as an engineering project with serious change-management discipline. We map your real customer journeys end-to-end, instrument the moments where data is lost today, and design a configuration that survives day-to-day sales pressure. Telephony, e-mail, billing, ticketing and analytics are integrated as a single platform rather than left as separate tabs in someone's browser.",
      "We deliver in short, demonstrable increments and pair every release with rep training, manager dashboards and a written admin runbook. The result is a CRM your team chooses to use because it actually makes their day shorter."
    ],
    deliverables: [
      "Sales, marketing, service and CPQ on a single platform",
      "Integrations with telephony, e-mail, ERP, accounting and analytics",
      "Pipeline forecasts, KPI dashboards and exec reporting packs",
      "Role-based access, audit trail and GDPR/KVKK governance",
      "User onboarding, admin handover and 12-month support retainer"
    ]
  },
  {
    slug: "software",
    code: "0x02",
    title: "Custom Software Development",
    blurb: "Web, mobile and back-office software engineered around your differentiating workflows.",
    intro: "When packaged software stops fitting the way you work, we build the application that does — from internal portals to customer-facing platforms.",
    body: [
      "Off-the-shelf tools cover obvious patterns, but every serious organisation eventually has processes worth software designed around them rather than the other way around. BTCRuka builds that software with a mainstream, durable stack chosen to outlive any individual engineer on the team.",
      "We deliver in short increments so stakeholders see working software early. Architecture, UX, build, test, deployment and observability are treated as one workstream, not handed across teams. Pipelines are set up from day one — code reviewed, tested, released and monitored from the first sprint.",
      "Engagements scale from a focused MVP through to long-running platform teams. Either way we leave behind documented code, predictable releases and a team that can defend the decisions we made together."
    ],
    deliverables: [
      "Discovery workshops, product blueprints and clickable prototypes",
      "React, Node, .NET, Python and mobile (React Native / iOS / Android)",
      "API-first architecture, integrations and webhooks",
      "Automated test suites, CI/CD pipelines and observability stack",
      "Long-term maintenance and SLA-backed support retainer"
    ]
  },
  {
    slug: "network",
    code: "0x03",
    title: "Network Infrastructure",
    blurb: "Wired, wireless and SD-WAN that disappears from the agenda — because it actually works.",
    intro: "From a single office to multi-site enterprise networks, we plan, install and operate the connectivity layer that keeps everything else running.",
    body: [
      "A network is either invisible or it becomes the single largest source of operational friction. We build networks that stay invisible — predictable, resilient and instrumented enough that issues are caught before users feel them.",
      "Our engineers handle structured cabling, switching, routing, enterprise Wi-Fi 6 / 6E, segmentation, SD-WAN between sites and secure remote access for distributed teams. We are vendor-pragmatic: hardware and software is chosen against what your environment needs, not against certifications we want to renew.",
      "Every deployment is documented down to the port and handed over with monitoring already in place. Customers know what they own, where it lives and how it is performing — three things that are surprisingly rare in the field."
    ],
    deliverables: [
      "Site survey, capacity planning and structured cabling",
      "Enterprise switching, routing and high-density Wi-Fi 6 / 6E",
      "SD-WAN, MPLS replacement and multi-site connectivity",
      "VLAN segmentation, zero-trust access and secure remote VPN",
      "End-to-end documentation, monitoring and proactive alerting"
    ]
  },
  {
    slug: "voice",
    code: "0x04",
    title: "Cloud PBX & IP Telephony",
    blurb: "Modern voice that works from anywhere, integrates with everything and costs less than legacy lines.",
    intro: "IP-based switchboard and contact-centre platforms that connect mobile, desktop and physical phones into a single intelligent voice system.",
    body: [
      "Voice is still the channel where customers expect a human. The technology underneath, however, has fundamentally changed: traditional PBX systems are being replaced by IP-based platforms that follow users rather than being tied to a desk.",
      "We deploy cloud and hybrid PBX systems that route calls intelligently across mobile apps, soft-phones, desk phones and contact-centre queues. Numbers are portable, recordings are searchable, and every conversation is available to your CRM the moment it ends.",
      "The migration plan is sequenced to avoid surprises: numbers ported under a documented runbook, dial plans built in stages, IVR scripts tested with real users before go-live, and a measured roll-back path retained for the first month."
    ],
    deliverables: [
      "Cloud-hosted and on-premise IP PBX deployments",
      "SIP trunking, number porting and international DID management",
      "Mobile, desktop and desk-phone clients on a single extension",
      "IVR menus, call queues, recording, transcription and analytics",
      "Native integration with CRM, helpdesk and ticketing systems"
    ]
  },
  {
    slug: "security",
    code: "0x05",
    title: "Cyber Security",
    blurb: "A continuous defensive practice — endpoints, identities, networks and cloud workloads.",
    intro: "We harden, monitor and respond against modern threats, and operate the practice on retainer so that defensive posture stays current week-to-week.",
    body: [
      "Cyber security is no longer an annual project. It is a continuous practice that has to keep pace with threats that change weekly. BTCRuka runs that practice on behalf of organisations that need credible defensive posture without building an in-house security team from scratch.",
      "We start from a defensible baseline: asset inventory, identity hygiene, e-mail and endpoint protection, patch discipline and segmentation. From there we layer controls calibrated to your risk profile — managed detection and response, vulnerability management, phishing simulation and DLP.",
      "When an incident happens, we are the team you call. Our IR process is rehearsed, documented and bounded by clear SLAs, so a bad day stays a bad day rather than escalating into an existential one."
    ],
    deliverables: [
      "Next-generation firewalls, EDR, e-mail and Wi-Fi hardening",
      "Identity and access management, MFA and conditional access",
      "Vulnerability scanning, patching and penetration testing",
      "24/7 monitoring, threat hunting and incident response on retainer",
      "Security awareness training and phishing simulation programmes"
    ]
  },
  {
    slug: "delivery",
    code: "0x06",
    title: "Project & Programme Delivery",
    blurb: "Senior delivery leadership for the technology programmes you cannot afford to drift.",
    intro: "We bring structured project and programme management to complex initiatives — scope, schedule, cost and risk under one accountable lead.",
    body: [
      "Technology programmes fail far more often from delivery discipline than from technical complexity. BTCRuka supplies senior project and programme managers who own the schedule, the budget, the dependencies and the difficult conversations.",
      "We work in whatever method fits the engagement — predictable waterfall for regulated programmes, Scrum or Kanban for product work, hybrid for the realistic middle ground most organisations actually live in. The constants are honest status reports, current risk registers and a single point of accountability.",
      "Our PMs come from the engineering side, not the spreadsheet side. They understand what they are managing, which means they negotiate scope on facts, catch issues months before they show up on a steering-committee dashboard, and write status reports stakeholders trust."
    ],
    deliverables: [
      "Programme initiation, business case shaping, stakeholder alignment",
      "Detailed plans, milestone tracking, RAID logs, steering reports",
      "Vendor management, contract negotiation, integration coordination",
      "Risk, quality, change and supplier management as continuous practice",
      "PMO setup, governance frameworks and post-implementation review"
    ]
  },
  {
    slug: "hosting",
    code: "0x07",
    title: "Web Hosting & Domains",
    blurb: "Hosting infrastructure, domains, SSL and DNS — quietly reliable, fully managed.",
    intro: "Managed hosting for marketing sites, content platforms, e-commerce and custom web applications — backed by real engineers, not a ticket portal.",
    body: [
      "Hosting feels like a commodity right up until something breaks at the wrong moment. BTCRuka runs hosting that doesn't break at the wrong moment — and when something does need attention, you have an engineer on the line, not an auto-responder.",
      "We provide managed hosting on infrastructure ranging from shared environments through dedicated VMs to fully isolated cloud accounts. We also handle the unglamorous but business-critical parts: domain registration, DNS management, SSL renewals, CDN configuration and backup verification.",
      "Every hosted service is backed by uptime monitoring, off-site backups and a documented recovery procedure. That sounds standard. In practice it is the difference between a half-hour incident and a half-week one."
    ],
    deliverables: [
      "Managed shared, VPS, dedicated and cloud hosting tiers",
      "Domain registration, DNS, SSL/TLS and CDN configuration",
      "Daily off-site backups with documented restore procedures",
      "Performance tuning, caching and core-web-vitals optimisation",
      "Uptime monitoring with human escalation 24/7"
    ]
  },
  {
    slug: "cloud",
    code: "0x08",
    title: "Cloud & DevOps",
    blurb: "Cloud architectures and delivery pipelines that move teams from monthly releases to daily ones.",
    intro: "We design, migrate and operate workloads on AWS, Azure and Google Cloud — automated, observable and cost-aware.",
    body: [
      "Cloud only pays back when it is treated as an engineering discipline rather than a hosting choice. BTCRuka helps organisations land on AWS, Azure or GCP in a way that compounds — infrastructure as code, automated deployments, observability and cost controls in place from day one.",
      "We work end-to-end: workload assessment, target architecture, migration execution, platform engineering and ongoing operations. For greenfield builds we set up the foundations — networking, identity, secrets, CI/CD, logging — that make later teams faster instead of slower.",
      "DevOps engagements are deliberately pragmatic. We bring strong opinions about automation and reliability, but we tune the practices to your team's size and maturity. The goal is always the same: smaller releases, shorter recovery, clearer ownership."
    ],
    deliverables: [
      "Cloud landing zones on AWS, Azure and GCP with IaC",
      "Lift-and-shift, re-platforming and re-architecting migration paths",
      "CI/CD pipelines, container platforms (Kubernetes, ECS, AKS) and GitOps",
      "Observability stacks: metrics, structured logs, traces and SLO tracking",
      "FinOps practices, cost dashboards and right-sizing programmes"
    ]
  },
  {
    slug: "data",
    code: "0x09",
    title: "Data Analytics & BI",
    blurb: "Pipelines, warehouses and dashboards that turn operational data into board-grade decisions.",
    intro: "From the first ETL pipeline to a full data platform, we build the analytics layer that makes leadership decisions evidence-based.",
    body: [
      "Every business already produces enough data to make better decisions. The gap is rarely the data itself — it is the pipelines, definitions and presentation layers that turn raw events into something a board can read on a Monday morning.",
      "BTCRuka builds data platforms that close that gap. We design data warehouses, implement ingestion from your operational systems, model the metrics that matter and deliver dashboards in tools your teams will actually open. Where it is justified, we layer machine-learning use cases on top — forecasting, segmentation, anomaly detection.",
      "We are deliberate about scope. A first phase typically targets three to five business questions, ships in weeks rather than quarters and earns the right to expand. The alternative — a multi-year data programme with no visible product — is the failure pattern we are explicitly built to avoid."
    ],
    deliverables: [
      "Cloud data warehouses on Snowflake, BigQuery and Redshift",
      "ELT pipelines (dbt, Fivetran, Airbyte) and reverse-ETL workflows",
      "Semantic layer modelling, KPI definitions and data governance",
      "Dashboards in Power BI, Looker, Tableau and Metabase",
      "Forecasting, segmentation and anomaly-detection models"
    ]
  },
  {
    slug: "advisory",
    code: "0x0A",
    title: "Technology Advisory",
    blurb: "Independent counsel for boards, founders and IT leaders who need clarity, not vendor pitches.",
    intro: "Strategy, architecture and technology due diligence delivered by senior practitioners with no vendor agenda.",
    body: [
      "Most technology decisions are made under uncertainty, under time pressure and under the influence of the loudest vendor in the room. BTCRuka offers independent, senior advisory precisely for the moments where those decisions matter most.",
      "Engagements include technology strategy alignment, target-state architecture, vendor selection, build-versus-buy analysis, technical due diligence for investments and post-merger IT integration planning. We deliver written recommendations that a board can read, defended by analysis an engineering team can verify.",
      "Our advisors have built and run the systems they advise on. That means our recommendations come with realistic timelines and honest trade-offs, not with the suspiciously confident slides that show up when accountability lies somewhere else."
    ],
    deliverables: [
      "Technology strategy, target operating model and architecture roadmaps",
      "Vendor evaluation, RFP design and contract negotiation",
      "Technical due diligence for M&A and investment decisions",
      "Post-merger IT integration and rationalisation planning",
      "Fractional CTO and senior advisory retainers"
    ]
  },
  {
    slug: "managed-it",
    code: "0x0B",
    title: "Managed IT Operations",
    blurb: "Outsourced IT operations — service desk, infrastructure, vendor management and monthly reporting.",
    intro: "A complete external IT department: tiered service desk, proactive monitoring, vendor coordination and quarterly business reviews under one SLA.",
    body: [
      "Building a high-functioning internal IT team is expensive, slow and difficult to retain. For most mid-market businesses, the better answer is a managed service partner that delivers the same outcomes against contracted service levels.",
      "BTCRuka operates as your IT department: a tiered service desk for user requests, proactive infrastructure monitoring, patch and asset management, vendor coordination for connectivity and software, and monthly reporting that translates IT activity into business language.",
      "Engagements are scoped against measurable SLAs — response times, resolution targets, uptime, satisfaction — and reviewed quarterly. As your business grows the service grows with it; if you decide later to bring IT back in-house, we hand over a documented, defensible operating model rather than a black box."
    ],
    deliverables: [
      "Tier-1 / Tier-2 service desk with multilingual support",
      "Endpoint management, patching, anti-virus, asset tracking",
      "Server, network and cloud infrastructure monitoring and ops",
      "Vendor management for ISPs, SaaS subscriptions and hardware",
      "Quarterly business reviews with KPIs, risks and improvement plans"
    ]
  }
];

export default services;
export const getService = (slug) => services.find((s) => s.slug === slug) || null;
